import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Search } from "lucide-react";
import { api } from "./lib";

type Hit = { type: string; id: string; title: string; sub: string; href: string };

// ⌘K anywhere: pages first, then everything you can see that matches. Arrow keys move, Return opens.
export function SearchBox({ pages }: { pages: { path: string; label: string; title?: string }[] }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<Hit[]>([]);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") (e.preventDefault(), setOpen(true));
      if (e.key === "/" && !/input|textarea|select/i.test((e.target as HTMLElement)?.tagName ?? "") && !(e.target as HTMLElement)?.isContentEditable) (e.preventDefault(), setOpen(true));
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) return setHits([]);
    const n = ++seq.current;
    setBusy(true);
    const t = setTimeout(() => {
      api(`/api/search?q=${encodeURIComponent(term)}`)
        .then((r) => n === seq.current && (setHits(r.results), setSel(0)))
        .finally(() => n === seq.current && setBusy(false));
    }, 160);
    return () => clearTimeout(t);
  }, [q]);

  const term = q.trim().toLowerCase();
  const pageHits: Hit[] = term ? pages.filter((p) => `${p.label} ${p.title ?? ""}`.toLowerCase().includes(term)).map((p) => ({ type: "Page", id: p.path, title: p.title ?? p.label, sub: "Go to page", href: "#" + p.path })) : [];
  const all = [...pageHits, ...hits];
  const close = () => (setOpen(false), setQ(""), setHits([]));
  const go = (h: Hit | undefined) => {
    if (!h) return;
    if (h.href.startsWith("http")) window.open(h.href, "_blank", "noopener");
    else location.hash = h.href.slice(1);
    close();
  };

  return (
    <>
      <button className="btn sm search-btn" onClick={() => setOpen(true)} aria-label="Search (⌘K)">
        <Search size={15} strokeWidth={2} aria-hidden /> Search <kbd>⌘K</kbd>
      </button>
      {open &&
        createPortal(
          <>
            <div className="drawer-scrim" onClick={close} />
            <div className="palette" role="dialog" aria-modal="true" aria-label="Search">
              <input
                className="palette-input"
                autoFocus
                placeholder="Search tasks, calendar, content, partners, PR, GitHub, Figma, people…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") close();
                  else if (e.key === "ArrowDown") (e.preventDefault(), setSel((s) => Math.min(all.length - 1, s + 1)));
                  else if (e.key === "ArrowUp") (e.preventDefault(), setSel((s) => Math.max(0, s - 1)));
                  else if (e.key === "Enter") go(all[sel]);
                }}
              />
              <div className="palette-results">
                {term.length < 2 ? (
                  <div className="muted small" style={{ padding: "14px 16px" }}>Type at least 2 letters. Press / or ⌘K from any page.</div>
                ) : !all.length ? (
                  <div className="muted small" style={{ padding: "14px 16px" }}>{busy ? "Searching…" : `Nothing matches "${q.trim()}".`}</div>
                ) : (
                  all.map((h, i) => (
                    <button key={h.type + h.id} className={`palette-row ${i === sel ? "on" : ""}`} onMouseEnter={() => setSel(i)} onClick={() => go(h)}>
                      <span className="palette-type">{h.type}</span>
                      <span className="palette-title">{h.title}</span>
                      {h.sub && <span className="palette-sub">{h.sub}</span>}
                    </button>
                  ))
                )}
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
